import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileSignature } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { listMyLetters } from '@/api/services/hrDocuments.service';
import { formatLetterDate } from './letterFormat';

/**
 * Dashboard card listing letters waiting for the viewer's signature. Everyone
 * sees it when they have something to sign, including HR and administrators
 * who sign on the company's behalf and have no self-service menu. Renders
 * nothing otherwise.
 */
export function LettersToSignCard(): React.ReactElement | null {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['my-letters'],
    queryFn: listMyLetters,
    retry: false,
  });
  const waiting = (Array.isArray(data) ? data : []).filter(
    (letter) => letter.awaitingMySignature
  );
  if (waiting.length === 0) return null;

  return (
    <Card
      header={
        <div className="flex items-center gap-2">
          <FileSignature className="h-4 w-4 text-primary" aria-hidden />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Letters to sign
          </h2>
          <span className="text-xs text-gray-500 dark:text-gray-400">{waiting.length}</span>
        </div>
      }
      contentClassName="p-0"
    >
      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
        {waiting.map((letter) => (
          <li
            key={letter._id}
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {letter.title}
              </p>
              {letter.expiresAt && (
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  Sign by {formatLetterDate(letter.expiresAt)}
                </p>
              )}
            </div>
            <Button size="sm" onClick={() => navigate(`/my-letters/${letter._id}`)}>
              Review and sign <span className="sr-only">{letter.title}</span>
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
